import React from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/faQuestionCircle";
import { faTimesCircle } from "@fortawesome/pro-regular-svg-icons/faTimesCircle";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import ImportErrorType from "@mapmycustomers/shared/enum/imports/ImportErrorType";
import ImportStatusEnum from "@mapmycustomers/shared/enum/imports/ImportStatus";
import File from "@mapmycustomers/shared/types/File";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Modal } from "@mapmycustomers/ui";

import { downloadFile } from "@app/store/files/actions";
import openChat from "@app/util/openChat";
import { getImportStatusDisplayName } from "@app/util/ui";

import ImportHistoryRow from "../type/ImportHistoryRow";
import getErrorTypeMessage from "../util/getErrorTypeMessage";
import getSkipFileName from "../util/getSkipFileName";

import styles from "./ImportStatus.module.scss";

const UiErrorCodes: ImportErrorType[] = [
  ImportErrorType.CSV_HEADER_MALFORMED,
  ImportErrorType.CSV_INCONSISTENT_RECORD_LENGTH,
  ImportErrorType.CSV_INVALID_CLOSING_QUOTE,
  ImportErrorType.CSV_NON_TRIMABLE_CHAR_AFTER_CLOSING_QUOTE,
  ImportErrorType.CSV_QUOTE_NOT_CLOSED,
  ImportErrorType.CSV_RECORD_DONT_MATCH_COLUMNS_LENGTH,
  ImportErrorType.EMPTY_FILE,
  ImportErrorType.ROW_OVERFLOW_ERROR,
];

const ImportErrorCodes: ImportErrorType[] = [
  ImportErrorType.UNKNOWN,
  ImportErrorType.VALIDATION_FAILED,
  ImportErrorType.IMPORT_ID_NOT_FOUND,
];
const messages = defineMessages({
  autoImportErrors: {
    id: "settings.importHistory.row.status.modal.autoImportError.title",
    defaultMessage: "Auto Import error(s)",
    description: "Title of status popover in row on imports history",
  },
  importErrors: {
    id: "settings.importHistory.row.status.modal.importError.title",
    defaultMessage: "Import error(s)",
    description: "Title of status popover in row on imports history",
  },
  partiallyCompleted: {
    id: "settings.importHistory.row.status.modal.partiallyCompletedImport.title",
    defaultMessage: "Import was partially completed",
    description: "Title of partially completed status modal in row on imports history",
  },
  partiallyCompletedText: {
    id: "settings.importHistory.row.status.modal.partiallyCompletedImport.content",
    defaultMessage:
      "This import was completed but one or more rows were skipped due to errors. Please download the skip file to fix your data and then reimport it.",
    description: "Content of partially completed import modal",
  },
});

interface Props {
  fileId?: File["id"];
  hasSkipFile: boolean;
  importHistoryRow: ImportHistoryRow;
  metadata: ImportHistoryRow["metadata"];
  onDownloadFile: typeof downloadFile;
  status: ImportStatusEnum;
}

const isUiError = (code: string) => UiErrorCodes.includes(code as ImportErrorType);
const isImportError = (code: string) => ImportErrorCodes.includes(code as ImportErrorType);

const ImportStatus: React.FC<Props> = ({
  fileId,
  hasSkipFile,
  importHistoryRow,
  metadata,
  onDownloadFile,
  status,
}) => {
  const intl = useIntl();
  const [isModalVisible, showModal, hideModal] = useBoolean();

  let tagColor = "default";

  if (status === ImportStatusEnum.COMPLETED) {
    tagColor = hasSkipFile ? "warning" : "success";
  } else if (status === ImportStatusEnum.ERRORED) {
    tagColor = "error";
  }

  const tag = (
    <Tag color={tagColor} onClick={showModal}>
      {getImportStatusDisplayName(intl, status)}
    </Tag>
  );
  let title = messages.importErrors;
  let content: React.ReactNode;

  if (
    (status === ImportStatusEnum.ERRORED && metadata.errorMessage) ||
    status === ImportStatusEnum.COMPLETED
  ) {
    if (status === ImportStatusEnum.COMPLETED && hasSkipFile) {
      title = messages.partiallyCompleted;
      content = (
        <div className={styles.error}>
          <div>{intl.formatMessage(messages.partiallyCompletedText)}</div>
        </div>
      );
    } else if (metadata.errorMessage) {
      if (isUiError(metadata.errorMessage.code) && Array.isArray(metadata.errorMessage.errors)) {
        title = messages.autoImportErrors;
        content = (
          <div className={styles.error}>
            {metadata.errorMessage.errors.map(({ code }) => {
              return (
                <>
                  <FontAwesomeIcon className={styles.icon} icon={faTimesCircle} />
                  <div>{getErrorTypeMessage(intl, code)}</div>
                </>
              );
            })}
          </div>
        );
      } else if (isImportError(metadata.errorMessage.code) && !hasSkipFile) {
        title = messages.importErrors;
        content = (
          <div className={styles.error}>
            <FontAwesomeIcon className={styles.icon} icon={faTimesCircle} />
            <div>{getErrorTypeMessage(intl, metadata.errorMessage.code as ImportErrorType)}</div>
          </div>
        );
      }
    }

    if (content) {
      return (
        <>
          <Tooltip
            placement="left"
            title={intl.formatMessage({
              id: "settings.importHistory.row.status.error.tooltip",
              defaultMessage: "Click for details",
              description: "Tooltip for Import status",
            })}
            trigger={
              status === ImportStatusEnum.ERRORED ||
              (status === ImportStatusEnum.COMPLETED && hasSkipFile)
                ? ["hover"]
                : []
            }
          >
            <Tag className={styles.tag} color={tagColor} onClick={showModal}>
              {getImportStatusDisplayName(intl, status)}
            </Tag>
          </Tooltip>
          <Modal
            footer={
              <div className={styles.footer}>
                <div>
                  {metadata.errorMessage &&
                  status === ImportStatusEnum.ERRORED &&
                  isUiError(metadata.errorMessage.code) ? (
                    <div className={styles.autoImportFooterText}>
                      {intl.formatMessage({
                        id: "settings.importHistory.row.status.modal.partiallyCompletedImport.footer.autoImport",
                        defaultMessage: "Contact your internal SFTP administrator for more details",
                        description:
                          "Title of partially completed status modal in row on imports history",
                      })}
                    </div>
                  ) : (
                    <Button
                      className={styles.footerText}
                      leftIcon={<FontAwesomeIcon icon={faQuestionCircle} />}
                      onClick={openChat}
                      target="_blank"
                      type="link"
                    >
                      {intl.formatMessage({
                        id: "settings.importHistory.row.status.modal.partiallyCompletedImport.footer.error",
                        defaultMessage: "Contact Support",
                        description:
                          "Title of partially completed status modal in row on imports history",
                      })}
                    </Button>
                  )}
                </div>
                {fileId ? (
                  <Button
                    leftIcon={<FontAwesomeIcon icon={faDownload} />}
                    onClick={() =>
                      onDownloadFile({
                        fileId,
                        fileName: getSkipFileName(intl, importHistoryRow),
                      })
                    }
                    type="primary"
                  >
                    {intl.formatMessage({
                      id: "settings.importHistory.row.status.modal.partiallyCompletedImport.footer.download.skipFile",
                      defaultMessage: "Skip File",
                      description:
                        "Download Skip file button for Partially completed Import on Info Modal",
                    })}
                  </Button>
                ) : (
                  <Button onClick={hideModal} type="primary">
                    {intl.formatMessage({
                      id: "settings.importHistory.row.status.modal.partiallyCompletedImport.footer.okText",
                      defaultMessage: "Ok",
                      description:
                        "Title of partially completed status modal in row on imports history",
                    })}
                  </Button>
                )}
              </div>
            }
            onCancel={hideModal}
            onOk={hideModal}
            open={isModalVisible}
            title={
              <div className={styles.title}>
                <div className={cn(styles.icon, styles.errored)}>
                  {status === ImportStatusEnum.COMPLETED && hasSkipFile ? (
                    <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
                  ) : (
                    <FontAwesomeIcon icon={faTimesCircle} />
                  )}
                </div>
                <div>{intl.formatMessage(title)}</div>
              </div>
            }
          >
            <div className={cn({ [styles.content]: !hasSkipFile })}>{content}</div>
          </Modal>
        </>
      );
    }
  }
  return tag;
};

export default ImportStatus;
